import { createSelector } from 'reselect';
import { find, get, isNil } from 'lodash';
import { getUserInitials } from 'modules/user/utils/user';
import { PENDING } from 'modules/common/constants/conversationStatuses';
import requestsStatuses from 'modules/common/constants/requestsStatuses';
import { FACE_TO_FACE } from 'modules/queues/constants/conversationTypes';
import { appointmentHavePermission } from 'modules/common/selectors/permissions';
import { dialerTypes } from 'modules/common/constants/conversationInitiatorTypes';
import * as appointmentStatuses from 'modules/common/constants/appointmentStatuses';
import { selfAvailability } from 'modules/queues/common/selectors/selfAvailability';
import { PROVIDER, CUSTOMER } from 'modules/common/constants/conversationParticipantsTypes';
import { conferenceInProgress, isInWaitingRoom } from 'modules/conference/selectors/consultationInfo';
import { getAppointmentsEstimatedTimeToBeServedSelector } from 'modules/queues/Schedule/selectors/appointments';

import {
  APPOINTMENTS_PROVIDER_AVAILABILITY_STATUS,
  APPOINTMENTS_CUSTOMER_AVAILABILITY_STATUS,
} from 'modules/common/constants/appointmentPermissionResources';

import {
  getName,
  getGender,
  getGenderDetails,
} from 'modules/appointmentInfo/utils/participantInfo';

import {
  getCurrentAppointment,
  getMissingFilesMessage,
  getMissingCaseMessage,
  getConversationParticipant,
} from './currentAppointment';


export const getAppointmentProvider = createSelector(
  getCurrentAppointment,
  appointment => {

    const providerProfile = find(appointment.participants, { participantType: PROVIDER });
    const providerGroupSpecialty = appointment.providerGroupSpecialty;
    const assignedToGroup = isNil(providerProfile) && !providerGroupSpecialty;
    const isClosed = appointmentStatuses.CLOSED_APPOINTMENT_STATUSES.includes(appointment.status);
    const canSeeProviderAvailability =
      appointmentHavePermission(appointment, { [APPOINTMENTS_PROVIDER_AVAILABILITY_STATUS]: ['read'] })
      && appointment.service.config.providerAvailabilityEnabled;
    const availabilityStatus = get(providerProfile, 'userAvailability.status');
    const provider = (assignedToGroup || providerGroupSpecialty) ? null : {
      ...providerProfile.user.profile,
      id: providerProfile.user.userId,
      name: getName(providerProfile.user.profile, { withMiddleName: true }),
      initials: getUserInitials(providerProfile.user.profile),
      type: providerProfile.participantType,
      policyId: providerProfile.user.policyId,
      availability: canSeeProviderAvailability && !isClosed && !!availabilityStatus && (selfAvailability(providerProfile.user.userId, availabilityStatus)),
    };

    return {
      assignedToGroup,
      provider,
      providerGroupSpecialty,
    };
  },
);

export const getAppointmentCustomer = createSelector(
  [getCurrentAppointment, conferenceInProgress, isInWaitingRoom],
  appointment => {
    const customerProfile = find(appointment.participants, { participantType: CUSTOMER });
    const isCustomerPatient = get(customerProfile, 'user.type') === 'PATIENT';
    const isClosed = appointmentStatuses.CLOSED_APPOINTMENT_STATUSES.includes(appointment.status);
    const canSeeCustomerAvailability =
      appointmentHavePermission(appointment, { [APPOINTMENTS_CUSTOMER_AVAILABILITY_STATUS]: ['read'] })
      && appointment.service.config.customerAvailabilityEnabled;
    const availabilityStatus = get(customerProfile, 'userAvailability.status');
    const isPatientInWaitingRoom = appointment.conversations.some(conversation => conversation.status === PENDING);

    const customer = {
      ...customerProfile.user.profile,
      id: customerProfile.user.userId,
      name: getName(customerProfile.user.profile, { withMiddleName: true }),
      initials: getUserInitials(customerProfile.user.profile),
      gender: getGenderDetails(getGender(customerProfile.user.profile)),
      availability: canSeeCustomerAvailability && !isClosed && !!availabilityStatus && (selfAvailability(customerProfile.user.userId, availabilityStatus)),
      isPatientInWaitingRoom,
    };

    return {
      isCustomerPatient,
      customer,
    };
  },
);

export const getAppointmentSubject = createSelector(
  getCurrentAppointment,
  (appointment) => {
    const subjectProfile = get(appointment, 'subjects[0]');

    return {
      ...subjectProfile.user.profile,
      id: subjectProfile.user.userId,
      name: getName(subjectProfile.user.profile, { withMiddleName: true }),
      initials: getUserInitials(subjectProfile.user.profile),
      gender: getGenderDetails(getGender(subjectProfile.user.profile)),
      description: subjectProfile.description,
    };
  },
);

export const getAppointmentSchedule = createSelector(
  getCurrentAppointment,
  appointment => {
    const startTime = get(appointment, 'schedules[0].startTime');
    const endTime = get(appointment, 'schedules[0].endTime');

    return {
      startTime: String(startTime),
      endTime: String(endTime),
    };
  },
);

const findParentId = units =>
  units.reduce((acc, cur) => {
    if (!acc) return cur.parentId;
    return (acc === cur.orgNodeId) ? cur.parentId : acc;
  }, undefined);


export const getProvidingUnitHierarchy = (units, parentId) => {
  const tempParentId = parentId || findParentId(units);
  const currentOrganization = units.find(unit => unit.parentId === tempParentId);

  if (isNil(currentOrganization)) return [];

  if (isNil(currentOrganization.name)) {
    return [...getProvidingUnitHierarchy(units, currentOrganization.orgNodeId)];
  }

  return [
    currentOrganization.name,
    ...getProvidingUnitHierarchy(units, currentOrganization.orgNodeId),
  ];
};

export const hierarchyToString = (hierarchy) => hierarchy.join(' > ');

export const getProvidingUnit = createSelector(
  getCurrentAppointment,
  appointment => hierarchyToString(getProvidingUnitHierarchy(appointment.service.providingUnit)),
);

const getQueueType = createSelector(
  getCurrentAppointment,
  appointment => get(appointment, 'service.config.queueType'),
);

export const getAppointmentDetails = createSelector(
  getCurrentAppointment,
  getAppointmentProvider,
  getAppointmentCustomer,
  getAppointmentSubject,
  getAppointmentSchedule,
  getProvidingUnit,
  getQueueType,
  getAppointmentsEstimatedTimeToBeServedSelector,
  getMissingFilesMessage,
  getMissingCaseMessage,
  (
      appointment,
      providerData,
      customer,
      subject,
      schedule,
      providingUnit,
      queueType,
      appointmentsEstimatedTimeToBeServedSelector,
      missingFilesMessage,
      missingCaseMessage,
   ) => {
    const { service, appointmentId, externalId, faceToFaceType } = appointment;
    const isClosed = appointmentStatuses.CLOSED_APPOINTMENT_STATUSES.includes(appointment.status);
    const closeTime = isClosed ? get(appointment, 'completedTimestamp', null) : null;
    const closeReason = isClosed ? appointment.status : '';
    const isF2F = appointment.allowedConversationTypes[0] === FACE_TO_FACE;

    return {
      service: {
        ...service,
        providingUnit: {
          name: providingUnit,
          logo: appointment.service.providingUnit[0].logo,
        },
      },

      appointmentId,
      externalId,
      queueType,

      ...providerData,
      ...customer,
      subject,

      ...schedule,
      isClosed,
      closeReason,
      closeTime: closeTime ? String(closeTime) : null,
      missingFilesMessage,
      missingCaseMessage,
      isF2F,
      faceToFaceType,
    };
  },
);

export const getCallerName = (state, intl) => {
  if (state.appointmentInfo.currentAppointment.requestStatus === requestsStatuses.default) return null;

  const { initiator } = state.conference.currentConversation;
  const isDialer = dialerTypes.includes(initiator) && isInWaitingRoom(state);
  const callerName = state.conference.currentConversation?.call?.callerName;
  const appointment = getCurrentAppointment(state);
  const healthcareName = hierarchyToString(getProvidingUnitHierarchy(appointment?.service?.providingUnit || []));
  const clinician = getConversationParticipant(state);
  const providerProfile = find(appointment.participants, { participantType: PROVIDER });
  const providerGroupSpecialty = appointment.providerGroupSpecialty;
  const assignedToGroup = isNil(providerProfile) && !providerGroupSpecialty;
  const clinicianFirstName =
    isDialer
      ? callerName || healthcareName
      : assignedToGroup
        ? intl.formatMessage({
          id: 'General.ProvidersGroup',
          defaultMessage: '** Providers group',
        })
        : clinician?.firstName;
  const clinicianLastName = !assignedToGroup && !isDialer ? clinician?.lastName : null;

  if (clinicianLastName) return clinicianFirstName.concat(' ', clinicianLastName);

  return clinicianFirstName;
};
